import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import BatchStatusSummaryTable from '../../components/BatchStatusSummaryTable';
import {
  deliveryStatusBadgeStyle,
  deliveryStatusLabel,
  formatStatusLabel,
  filterCoursesByStudentStatus,
} from '../../utils/batchStudentStatusHelpers';

const PRINT_STYLES = `
@media print {
  @page { size: A4 portrait; margin: 16mm 14mm; }
  .print-hide { display: none !important; }
  body { background: #fff !important; }
  #batch-student-status-print table { page-break-inside: auto; }
  #batch-student-status-print tr { page-break-inside: avoid; break-inside: avoid; }
  #batch-student-status-print .print-course-block:not(:first-child) { page-break-before: always; break-before: page; }
  /* Keep the tail of the last batch's table together with the sign-off block
     so the signature never lands alone at the top of a fresh page. */
  #batch-student-status-print .print-signoff-group { page-break-inside: avoid; break-inside: avoid; }
}
`;

function formatScheduleType(scheduleType) {
  if (!scheduleType) return '';
  return scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1);
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function BatchStatusTable({ batch }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-800">
          {batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch'}
          <span className="ml-2 font-normal text-gray-500">({formatStatusLabel(batch.status)})</span>
        </p>
        <span className="text-xs text-gray-500">
          {formatDate(batch.batchStartingDate)} – {formatDate(batch.batchEndingDate)}
        </span>
      </div>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border border-gray-400 bg-gray-100">
            <th className="border border-gray-400 px-2 py-1 text-left">#</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Student Name</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Student ID</th>
            <th className="border border-gray-400 px-2 py-1 text-left">Reg. No.</th>
            <th className="border border-gray-400 px-2 py-1 text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          {batch.students.map((s, idx) => (
            <tr key={`${s.studentId}-${s.registrationNumber}`}>
              <td className="border border-gray-400 px-2 py-1">{idx + 1}</td>
              <td className="border border-gray-400 px-2 py-1">{s.studentName || '—'}</td>
              <td className="border border-gray-400 px-2 py-1">{s.studentId || '—'}</td>
              <td className="border border-gray-400 px-2 py-1">{s.registrationNumber || '—'}</td>
              <td className="border border-gray-400 px-2 py-1 text-center font-semibold" style={deliveryStatusBadgeStyle(s.deliveryStatus)}>
                {deliveryStatusLabel(s.deliveryStatus)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BatchStudentStatusPrintView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const courseIdsParam = searchParams.get('courseIds') || 'all';
  const batchStatusParam = searchParams.get('batchStatus') || 'ongoing';
  const studentStatusParam = searchParams.get('studentStatus') || 'all';
  const studentStatusValues = studentStatusParam === 'all' ? [] : studentStatusParam.split(',').filter(Boolean);
  const viewParam = searchParams.get('view') === 'detailed' ? 'detailed' : 'summary';

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        const accessData = accessRes?.data || {};
        const isSuperAdmin = !!accessData.isSuperAdmin;
        const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';
        const accessibleCourseIds = Array.isArray(accessData.accessibleCourseIds) ? accessData.accessibleCourseIds : [];

        const accessibleParam = isSuperAdmin || isManagement
          ? 'all'
          : accessibleCourseIds.map((id) => Number(id)).filter((num) => Number.isFinite(num) && num > 0);

        const resp = await axiosInstance.get('/admin/dashboard/batch-student-status', {
          params: {
            accessibleCourses: accessibleParam,
            courseIds: courseIdsParam,
            batchStatus: batchStatusParam,
          },
        });
        if (!isMounted) return;

        const data = resp?.data || {};
        setCourses(data.courses || []);
        setGeneratedAt(data.generatedAt || new Date().toISOString());
      } catch (err) {
        if (isMounted) setError(err?.response?.data?.message || 'Failed to load batch & student status report');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open the print dialog once the document has rendered
  useEffect(() => {
    if (loading || error) return;
    const timer = setTimeout(() => window.print(), 400);
    return () => clearTimeout(timer);
  }, [loading, error]);

  // The Summary Table breaks every student status down as its own column, so
  // it always shows the full, unfiltered breakdown regardless of the Student
  // Status filter — that filter only narrows the Detailed List view.
  const displayCourses = useMemo(
    () => (viewParam === 'summary' ? courses : filterCoursesByStudentStatus(courses, studentStatusValues)),
    [courses, studentStatusValues, viewParam]
  );

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <div className="min-h-screen bg-gray-100">
        <div className="print-hide sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 shadow-sm">
          <span className="text-sm font-medium text-gray-600">Print preview — Active Batches &amp; Student Status Report</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-lg border border-emerald-600 bg-emerald-600 px-4 py-2 text-xs font-medium text-white hover:bg-emerald-700"
            >
              Print
            </button>
            <button
              type="button"
              onClick={() => window.close()}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>

        <div id="batch-student-status-print" className="mx-auto max-w-4xl bg-white p-10 shadow-sm print:shadow-none">
          {loading ? (
            <p className="text-center text-gray-500">Loading…</p>
          ) : error ? (
            <p className="text-center text-red-600">{error}</p>
          ) : (
            <>
              {displayCourses.length === 0 && (
                <p className="text-center text-sm text-gray-500">No batches match the applied filters.</p>
              )}
              {displayCourses.map((course) => {
                const courseStudentCount = course.batches.reduce((sum, b) => sum + b.students.length, 0);
                const courseBatchNames = course.batches
                  .map((b) => b.batchInternalName || b.whatsappGroupName || b.batchName || 'Batch')
                  .join(', ');
                return (
                <div key={course.courseId ?? course.courseName} className="print-course-block">
                  {/* Letterhead — repeated per course so each course prints as its own standalone report */}
                  <div className="border-b-2 border-gray-800 pb-4 text-center">
                    <h1 className="text-2xl font-black tracking-wide text-gray-900">ESOFT METRO CAMPUS BATTICALOA</h1>
                    <p className="mt-0.5 text-xs uppercase tracking-widest text-gray-500">Academic Affairs Division</p>
                  </div>

                  <div className="mt-5 text-center">
                    <span className="inline-block rounded-sm border border-gray-800 px-4 py-1 text-xs font-bold uppercase tracking-widest text-gray-800">
                      Official Notice
                    </span>
                    <h2 className="mt-3 text-xl font-bold text-gray-900">Active Batches &amp; Student Status Report</h2>
                    <p className="mt-1 text-base font-semibold text-gray-800">
                      {course.courseShortName || course.courseName || 'Unknown Course'}
                      {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
                    </p>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-1 border border-gray-300 px-4 py-3 text-xs text-gray-700">
                    <div><span className="font-semibold">Generated:</span> {generatedAt ? new Date(generatedAt).toLocaleString('en-GB') : '—'}</div>
                    <div><span className="font-semibold">Students listed:</span> {courseStudentCount}</div>
                    <div>
                      <span className="font-semibold">Batch stage:</span>{' '}
                      {batchStatusParam === 'all' ? 'All stages' : formatStatusLabel(batchStatusParam)}
                    </div>
                    {viewParam === 'detailed' && (
                      <div>
                        <span className="font-semibold">Student status:</span>{' '}
                        {studentStatusValues.length === 0 ? 'All statuses' : studentStatusValues.map((v) => formatStatusLabel(v)).join(', ')}
                      </div>
                    )}
                    <div>
                      <span className="font-semibold">Batches covered:</span> {course.batches.length}
                    </div>
                    <div className="col-span-2">
                      <span className="font-semibold">Batch names:</span> {courseBatchNames || '—'}
                    </div>
                  </div>

                  {viewParam === 'summary' ? (
                    <div className="print-signoff-group mt-6 space-y-5">
                      <BatchStatusSummaryTable course={course} />

                      <div className="mt-8 grid grid-cols-2 gap-10 text-xs text-gray-600">
                        <div>
                          <div className="h-16"></div>
                          <div className="border-t border-gray-800 pt-1">Prepared by — Signature &amp; Date</div>
                        </div>
                        <div>
                          <div className="h-16"></div>
                          <div className="border-t border-gray-800 pt-1">Head of Department — Signature &amp; Date</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-6 space-y-5">
                      {course.batches.slice(0, -1).map((batch) => (
                        <BatchStatusTable key={batch.batchId} batch={batch} />
                      ))}

                      {/* Last batch's table is grouped with the sign-off block so the
                          signature never ends up alone at the top of a fresh page. */}
                      <div className="print-signoff-group space-y-5">
                        {course.batches.length > 0 && (
                          <BatchStatusTable
                            key={course.batches[course.batches.length - 1].batchId}
                            batch={course.batches[course.batches.length - 1]}
                          />
                        )}

                        <div className="mt-8 grid grid-cols-2 gap-10 text-xs text-gray-600">
                          <div>
                            <div className="h-16"></div>
                            <div className="border-t border-gray-800 pt-1">Prepared by — Signature &amp; Date</div>
                          </div>
                          <div>
                            <div className="h-16"></div>
                            <div className="border-t border-gray-800 pt-1">Head of Department — Signature &amp; Date</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </>
  );
}
